import React, { ChangeEvent, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, FormikHelpers } from "formik";
import * as Yup from "yup";

import { trimOnBlur } from "../../utils/trimOnBlur";
import check15 from "../../assets/large-purple-check-1.5x.png";
import check2 from "../../assets/large-purple-check-2x.png";
import check3 from "../../assets/large-purple-check-3x.png";
import check1 from "../../assets/large-purple-check-1x.png";

interface Props {
  open: boolean;
  title?: string;
  textLabel?: string;
  onSubmit: (feedback: FeedbackItem) => Promise<boolean>;
  submitText?: string;
  onCancel: () => void;
  cancelText?: string;
  confirmationText?: string;
  charLimit?: number;
  rows?: number;
  screenName?: string;
}

export type FeedbackItem = {
  feedbackType?: string;
  feedbackTypeOther?: string;
  feedback?: string;
  screenName?: string;
};

interface Values {
  feedbackType: string;
  feedbackTypeOther: string;
  feedback: string;
}

type FeedbackTypeOption = {
  value: string;
  label: string;
};

const OTHER_OPTION_VALUE = "Something else";

const feedbackTypeOptions: FeedbackTypeOption[] = [
  {
    value: "cant_find_form",
    label: "Can’t find a form",
  },
  {
    value: "need_to_change_probate",
    label: "Need to change my probate type/packet",
  },
  {
    value: "form_is_missing_from_packet",
    label: "A form is missing from my packet",
  },
  {
    value: "need_ancillary_estate",
    label: "Need to add another ancillary estate",
  },
  {
    value: "something_else",
    label: OTHER_OPTION_VALUE,
  },
];

export const FeedbackDialog = ({
  open,
  title,
  textLabel = "How we can help",
  onSubmit,
  submitText = "Send",
  onCancel,
  cancelText = "Cancel",
  charLimit = 1500,
  rows = 8,
  confirmationText = "Thanks! We’ll get back to you shortly",
  screenName,
}: Props): JSX.Element => {
  // const optionsRequiringFollowUp: string[] = [];
  // options.forEach((option: RadioOption) => {
  //   option.followUp && optionsRequiringFollowUp.push(option.value);
  // });

  const [charCount, setCharCount] = useState(0);
  const [confirmation, setConfirmation] = useState(false);
  const [showFeedbackOther, setShowFeedbackOther] = useState(false);

  // https://formik.org/docs/api/formik
  const formikInit = {
    values: {
      feedbackType: "",
      feedbackTypeOther: "",
      feedback: "",
    },
    schema: Yup.object().shape({
      feedbackType: Yup.string(),
      feedbackTypeOther: Yup.string(),
      feedback: Yup.string(),
    }),
  };

  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    if (
      values.feedback === "" ||
      values.feedbackType === "" ||
      (values.feedbackType === OTHER_OPTION_VALUE &&
        values.feedbackTypeOther === "")
    ) {
      setStatus("Please enter a subject and provide your feedback above.");
      setSubmitting(false);
      return;
    }
    try {
      if (
        await onSubmit({
          feedbackType: values.feedbackType,
          feedbackTypeOther: values.feedbackTypeOther,
          feedback: values.feedback,
          screenName: screenName,
        })
      ) {
        setConfirmation(true);
      }
    } catch (error) {
      setStatus("Sorry, error: " + ((error as Error)?.message || ""));
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onCancel();
    setTimeout(() => {
      setConfirmation(false);
    }, 200);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <IconButton
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
        }}
        aria-label="delete"
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        {confirmation ? (
          <Box
            sx={{
              textAlign: "center",
              p: 4,
              minWidth: 450,
            }}
          >
            <img
              srcSet={`${check15} 1.5x, ${check2} 2x, ${check3} 3x`}
              src={check1}
              alt="Checkmark"
              title="Checkmark"
            />
            <Typography
              variant="h4"
              sx={{ textAlign: "center", py: 2 }}
              gutterBottom={true}
            >
              {confirmationText}
            </Typography>
            <Button variant="contained" onClick={handleClose}>
              Close
            </Button>
          </Box>
        ) : (
          <>
            <Typography
              variant="h4"
              sx={{ textAlign: "center", maxWidth: "80%", marginX: "auto" }}
            >
              {title}
            </Typography>
            <DialogContentText id="alert-dialog-description" component="div">
              <Formik
                initialValues={formikInit.values}
                validationSchema={formikInit.schema}
                onSubmit={handleSubmit}
                // force re-render by effectively subscribing to specific value
                // key={objectVals2String(formikInit.values)}
              >
                {({
                  errors,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values,
                  status,
                  setFieldValue,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        my: 1,
                      }}
                    >
                      <Grid item xs={12}>
                        <TextField
                          variant="outlined"
                          name="feedbackType"
                          // id=""
                          select
                          label="Select subject"
                          value={values.feedbackType}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            if (event.target.value === OTHER_OPTION_VALUE) {
                              setShowFeedbackOther(true);
                            } else {
                              setFieldValue("feedbackTypeOther", "");
                              setShowFeedbackOther(false);
                            }
                            handleChange(event);
                          }}
                          error={
                            touched.feedbackType && Boolean(errors.feedbackType)
                          }
                          helperText={
                            touched.feedbackType && errors.feedbackType
                          }
                          sx={{
                            width: "100%",
                          }}
                        >
                          {feedbackTypeOptions.map(
                            (option: FeedbackTypeOption) => (
                              <MenuItem key={option.value} value={option.label}>
                                {option.label}
                              </MenuItem>
                            )
                          )}
                        </TextField>
                        {showFeedbackOther && (
                          <TextField
                            fullWidth
                            inputProps={{ maxLength: 80 }}
                            label="Other subject"
                            margin="normal"
                            name="feedbackTypeOther"
                            onBlur={(evt: React.FocusEvent<any>) => {
                              trimOnBlur(
                                evt,
                                "text",
                                setFieldValue,
                                handleBlur
                              );
                            }}
                            onChange={handleChange}
                            type="text"
                            value={values.feedbackTypeOther}
                            variant="outlined"
                          />
                        )}
                        <TextField
                          multiline={true}
                          error={
                            Boolean(touched.feedback && errors.feedback) ||
                            (charLimit && charCount > charLimit ? true : false)
                          }
                          fullWidth
                          rows={rows}
                          helperText={
                            charLimit
                              ? `${charLimit - charCount} characters left`
                              : ""
                          }
                          label={textLabel || "Text"}
                          margin="normal"
                          name="feedback"
                          onBlur={(evt: React.FocusEvent<any>) => {
                            trimOnBlur(evt, "text", setFieldValue, handleBlur);
                          }}
                          onChange={(event) => {
                            setCharCount(event.target.value.length);
                            handleChange(event);
                          }}
                          type="text"
                          value={values.feedback}
                          variant="outlined"
                        />
                        <Typography
                          variant="body1"
                          color="error"
                          sx={{
                            textAlign: "center",
                          }}
                        >
                          {status}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        autoFocus
                        disabled={isSubmitting}
                      >
                        {submitText}
                      </Button>
                      <Button onClick={() => onCancel()} color="primary">
                        {cancelText}
                      </Button>
                    </Box>
                  </form>
                )}
              </Formik>
            </DialogContentText>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
